import {
  Button,
  Dialog,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { isEmpty } from 'lodash';
import { useGlobalCore } from '../../../../../core/modules/global';
import { UPDATE_CATEGORY_DIALOG } from '../../../../../core/constants/dialog';
import Spinner from '../../../Spinner';
import { tType, useMasterCore } from '../../../../../core/modules/master';
import { useCompanyCore } from '../../../../../core/modules/company';
import Swal from 'sweetalert2';
import { Type } from '../../../../../core/interfaces/type.interface';
import { Category, ContainerCategory, ProductCategory } from '../../../../../core/interfaces/category.interface';
import { TypeLabel } from './CreateDialog';

interface Props {
  type: tType;
  types: Type[];
  parent_id: number | null;
  onClose: () => void;
}

const schema = yup.object().shape({
  name: yup.string().required('Campo requerido'),
  description: yup.string().required('Campo requerido'),
  type_id: yup.number().when('parent_id', {
    is: (parent_id: number | null) => !parent_id,
    then: (schema) => schema.required('Campo requerido'),
    otherwise: (schema) => schema.optional(),
  }),
  parent_id: yup.number().nullable().optional(),
});

const UpdateDialog = (props: Props) => {
  const { type, types, parent_id, onClose } = props;
  const { dialogs, closeDialog } = useGlobalCore();
  const { category, putCategory, deleteCategory, getCategories, setCategory, productCategories } = useMasterCore();
  const { companyId } = useCompanyCore();

  const [loading, setLoading]  = useState(false);

  const { control, formState, handleSubmit, resetField, setValue, watch } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
  });
  const { isValid, errors } = formState;
  
  // Watch parent_id to disable type selection when parent is selected
  const watchParentId = watch('parent_id');
  
  // Cuando cambia el parent_id, actualizar el type_id con el del padre
  useEffect(() => {
    if (watchParentId && type === 'product') {
      const findCategoryRecursive = (categories: Category[]): ProductCategory | null => {
        for (const cat of categories) {
          if (cat.id === watchParentId) {
            return cat as ProductCategory;
          }
          const productCategory = cat as ProductCategory;
          if (productCategory.child_categories && productCategory.child_categories.length > 0) {
            const found = findCategoryRecursive(productCategory.child_categories);
            if (found) {
              return found;
            }
          }
        }
        return null;
      };
      const parentCategory = findCategoryRecursive(productCategories);
      if (parentCategory && parentCategory.product_type_id) {
        setValue('type_id', parentCategory.product_type_id);
      }
    }
  }, [watchParentId, type, setValue, productCategories]);

  const resetForm = useCallback(() => {
    resetField('name');
    resetField('description');
    resetField('type_id');
    resetField('parent_id');
    setCategory(null);
    onClose();
    closeDialog(UPDATE_CATEGORY_DIALOG);
  }, [onClose, closeDialog, resetField, setCategory]);

  useEffect(() => {
    if (category) {
      setValue("name", category.name);
      setValue("description", category.description);
      if (type === 'product') {
        const productCategory = category as ProductCategory;
        setValue("parent_id", productCategory.parent_id || null);
        // Si es una subcategoría, obtener el tipo del padre
        if (productCategory.parent_id) {
          const findCategoryRecursive = (categories: Category[]): ProductCategory | null => {
            for (const cat of categories) {
              if (cat.id === productCategory.parent_id) {
                return cat as ProductCategory;
              }
              const pc = cat as ProductCategory;
              if (pc.child_categories && pc.child_categories.length > 0) {
                const found = findCategoryRecursive(pc.child_categories);
                if (found) {
                  return found;
                }
              }
            }
            return null;
          };
          const parentCategory = findCategoryRecursive(productCategories);
          if (parentCategory && parentCategory.product_type_id) {
            setValue("type_id", parentCategory.product_type_id);
          } else if (productCategory.product_type_id) {
            // Si no encontramos el padre pero la categoría tiene tipo, usarlo
            setValue("type_id", productCategory.product_type_id);
          }
        } else {
          // Si es categoría raíz, usar su propio tipo
          setValue("type_id", productCategory.product_type_id);
        }
      } else {
        setValue("type_id", (category as ContainerCategory).container_type_id);
      }
    }
  }, [category, type, setValue, productCategories]);

  const onSubmit = useCallback(
    (values: any) => {
      if (companyId && category) {
        setLoading(true);
        const body = type === 'product' ? {
          ...values,
          parent_id: values.parent_id,
          product_type_id: values.parent_id ? undefined : values.type_id,
        } : {
          ...values,
          parent_id: parent_id,
          container_type_id: values.type_id,
        };

        putCategory(companyId, category.id, body, type, () => {
          resetForm();
          getCategories(companyId, type);
          setLoading(false);
        }, () => {
          setLoading(false);
        });
      }
    },
    [companyId, category, type, parent_id, putCategory, resetForm, getCategories]
  );

  const onDelete = useCallback(() => {
    Swal.fire({
      title: "Confimar acción",
      text: `Eliminar la categoría ${category?.name}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Si, eliminar!",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (result.isConfirmed && companyId && category) {
        setLoading(true);

        deleteCategory(
          companyId,
          category.id,
          type,
          () => {
            setLoading(false);
            getCategories(companyId, type);
            resetForm();
          },
          () => {
            setLoading(false);
          }
        );
      }
    });
  }, [category, companyId, type, deleteCategory, getCategories, resetForm]);

  return (
    <Dialog
      classes={{
        paper: 'm-8 sm:m-24 min-w-[350px]',
      }}
      onClose={resetForm}
      open={Array.isArray(dialogs) && dialogs.includes(UPDATE_CATEGORY_DIALOG)}
    >
      <DialogContent>
        <div className="flex flex-auto flex-col py-16 px-0 sm:px-16">
          <div className="flex flex-col">
            <div className="mb-16 flex items-center">
              <Typography className="text-2xl font-bold truncate">
                Modificar Categoría {TypeLabel[type]}
              </Typography>
            </div>
      
            <div className="w-full max-w-3xl mt-20">
              <div className="flex flex-col space-y-32">
                <form noValidate onSubmit={handleSubmit(onSubmit)}>
                  <div className="mb-24">
                    <Controller
                      name="name"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Nombre"
                          type="text"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.name}
                          helperText={errors?.name?.message}
                          required
                          fullWidth
                        />
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="description"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Descripción"
                          type="textarea"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.description}
                          helperText={errors?.description?.message}
                          required
                          fullWidth
                        />
                      )}
                    />
                  </div>

                  {type === 'product' && (
                    <div className="mb-24">
                      <Controller
                        name="parent_id"
                        control={control}
                        render={({ field }) => (
                          <FormControl variant="outlined" fullWidth>
                            <InputLabel id="parent-label">
                              Categoría Padre (Opcional)
                            </InputLabel>
                            <Select
                              {...field}
                              labelId="parent-label"
                              label="Categoría Padre (Opcional)"
                              disabled={loading}
                              value={field.value || ''}
                            >
                              <MenuItem value="">
                                <em>Sin categoría padre</em>
                              </MenuItem>
                                                             {(() => {
                                 // Función recursiva para encontrar todas las categorías en el árbol
                                 const getAllCategories = (categories: Category[]): Category[] => {
                                   let allCategories: Category[] = [];
                                   for (const cat of categories) {
                                     allCategories.push(cat);
                                     const productCategory = cat as ProductCategory;
                                     if (productCategory.child_categories && productCategory.child_categories.length > 0) {
                                       allCategories = allCategories.concat(getAllCategories(productCategory.child_categories));
                                     }
                                   }
                                   return allCategories;
                                 };

                                 const allCategories = getAllCategories(productCategories);
                                 
                                 return allCategories
                                   .filter(cat => {
                                     // Excluir la categoría actual
                                     if (cat.id === category?.id) {
                                       return false;
                                     }
                                     // Mostrar categorías raíz como opciones normales
                                     if (!(cat as ProductCategory).parent_id) {
                                       return true;
                                     }
                                     // Si hay un parent_id específico, también mostrar esa categoría
                                     if (parent_id && cat.id === parent_id) {
                                       return true;
                                     }
                                     return false;
                                   })
                                   .map((category) => (
                                     <MenuItem 
                                       key={category.id} 
                                       value={category.id}
                                                                               disabled={!!(parent_id && category.id === parent_id)}
                                     >
                                       {category.name}
                                       {parent_id && category.id === parent_id && ' (Seleccionado)'}
                                     </MenuItem>
                                   ));
                               })()}
                            </Select>
                          </FormControl>
                        )}
                      />
                    </div>
                  )}

                  <div className="mb-24">
                    <Controller
                      name="type_id"
                      control={control}
                      render={({ field }) => {
                        const hasParent = type === 'product' && (parent_id !== null || !!watchParentId);
                        return (
                          <FormControl variant="outlined" fullWidth>
                            <InputLabel id="type-label">
                              Tipo
                            </InputLabel>
                            <Select
                              {...field}
                              labelId="type-label"
                              label="Tipo"
                              disabled={loading || hasParent}
                              required={!parent_id && !watchParentId}
                              value={field.value || ''}
                            >
                              {types.map((type) => (
                                <MenuItem key={type.id} value={type.id}>{type.name}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        );
                      }}
                    />
                  </div>

                  <div className="flex justify-center gap-4">
                    <Button
                      variant="contained"
                      color="error"
                      className="w-full"
                      sx={{ borderRadius: 50 }}
                      disabled={loading}
                      type="button"
                      onClick={onDelete}
                    >
                      <Spinner show={loading} />
                      Eliminar
                    </Button>
                    <Button
                      variant="contained"
                      color="secondary"
                      className="w-full"
                      sx={{ borderRadius: 50 }}
                      disabled={!isEmpty(errors) || !isValid || loading}
                      type="submit"
                      size="large"
                    >
                      <Spinner show={loading} />
                      Guardar
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateDialog;
